import React, { useCallback, useMemo, useState } from 'react'
import clsx from 'clsx'
import { transparentize } from 'polished'
import { mutualTheme } from '~/utils/theme'
import { useDropzone } from 'react-dropzone'
import { makeStyles } from '@material-ui/styles'
import { Box, Typography } from '@material-ui/core'
import { DropInputIcon, InfoText, ResponsiveActionButtons } from './components'
import { ContentType, DocumentType } from '@mutual-services/account-documents-types'
import { useIsMobileScreen } from '~/hooks'

const MAX_UPLOAD_FILE_SIZE = 10000000 // 10 MB

const useStyles = makeStyles({
  dropArea: {
    borderRadius: 10,
    border: `2px dashed ${mutualTheme.palette.grey.A200}`
  },
  dropAreaCompleted: {
    borderColor: mutualTheme.palette.success.light,
    backgroundColor: mutualTheme.palette.pastelGreen.main
  },
  dropAreaAccept: {
    backgroundColor: transparentize(0.80, mutualTheme.palette.secondary.main)
  },
  dropAreaReject: {
    borderColor: mutualTheme.palette.error.main
  },
  fileTooLarge: {
    borderColor: mutualTheme.palette.error.main
  },
  label: {
    marginLeft: mutualTheme.spacing(2),
    fontWeight: mutualTheme.typography.fontWeightMedium,
    [mutualTheme.breakpoints.down('md')]: {
      marginLeft: mutualTheme.spacing(1)
    }
  },
  cursorStyle: (props: DropInputProps) => ({
    cursor: props.completedAt ? 'default' : 'pointer'
  }),
  container: {
    display: 'flex',
    flexDirection: 'column'
  },
  blueText: {
    color: mutualTheme.palette.secondary.main,
    fontWeight: mutualTheme.typography.fontWeightMedium
  },
  innerContainer: {
    flex: '1',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: mutualTheme.spacing(1),
    [mutualTheme.breakpoints.down('md')]: {
      padding: mutualTheme.spacing(0.5)
    }
  }
})

export type DropInputProps = {
  value?: File,
  name: string,
  label?: string,
  onBlur: () => void,
  canRemove?: boolean,
  maxSizeBytes?: number,
  fileTypes: ContentType[],
  defaultValue?: File | null,
  containerClassName?: string,
  completedAt?: Date | null | string,
  onChange?: (event: { target: { name: string, value: File } }) => void,
  allowedDocumentTypes: DocumentType[],
  fieldArrayProps?: {
    remove: (index?: number) => void
  },
  hasOnlyOneFile?: boolean,
  error?: any
}

export const DropInput = React.forwardRef<HTMLInputElement, DropInputProps>((props, ref) => {
  const { hasOnlyOneFile, canRemove, fileTypes, allowedDocumentTypes, label, onChange, name, value, onBlur, containerClassName, completedAt, defaultValue, fieldArrayProps } = props
  const [file, setFile] = useState<File | undefined | null>(value || defaultValue)
  const onFileDrop = useCallback((file: File[]) => {
    setFile(file[0])
    if (onChange) {
      onChange({
        target: {
          name,
          value: file[0]
        }
      })
    }
  }, [name, onChange])

  const onFileDropRejected = useCallback(() => {
    setFile(null)
  }, [setFile])

  const onRemoveItemClick = useCallback(() => {
    if (file) {
      if (onChange) {
        onChange({
          target: {
            name,
            value: null as any
          }
        })
      }
      setFile(null)
      return
    }

    fieldArrayProps && fieldArrayProps.remove()
  }, [setFile, hasOnlyOneFile, fieldArrayProps, file, onChange, name])

  const { getRootProps, getInputProps, isDragAccept, isDragReject, fileRejections } = useDropzone({
    minSize: 0,
    maxFiles: 1,
    accept: fileTypes,
    onDropAccepted: onFileDrop,
    onDropRejected: onFileDropRejected,
    maxSize: MAX_UPLOAD_FILE_SIZE
  })

  const classes = useStyles(props)
  const isMobile = useIsMobileScreen()

  const hasFile = useMemo(() => Boolean(file), [file])
  const isCompleted = useMemo(() => Boolean(completedAt), [completedAt])
  const spaceBetween = useMemo(() => label && { justifyContent: 'space-between' }, [label])
  const isFileTooLarge = useMemo(() => fileRejections.length > 0 && fileRejections[0].file.size > MAX_UPLOAD_FILE_SIZE, [fileRejections])

  return (
    <Box className={classes.container}>
      <Box
        className={clsx(classes.dropArea, containerClassName, {
          [classes.fileTooLarge]: isFileTooLarge,
          [classes.dropAreaAccept]: isDragAccept,
          [classes.dropAreaReject]: isDragReject,
          [classes.dropAreaCompleted]: isCompleted || hasFile
        })}
        sx={{
          display: 'flex',
          flexDirection: 'row'
        }}>
        <Box className={clsx(classes.cursorStyle, classes.innerContainer)}>
          <Box {...(isCompleted ? {} : getRootProps())} sx={{ display: 'flex', flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <input ref={ref} name={name} onBlur={onBlur} {...getInputProps()} />
              <DropInputIcon hasFile={hasFile} isCompleted={isCompleted} />
              <Box sx={{ display: 'flex', flex: 1, flexDirection: isMobile ? 'column' : 'row', ...spaceBetween }}>
                <Typography variant={isMobile ? 'airbnbBody2' : 'airbnbBody1'} className={classes.label}>{label}</Typography>
                <InfoText
                  file={file}
                  completedAt={completedAt}
                  isFileTooLarge={isFileTooLarge}
                  allowedDocumentTypes={allowedDocumentTypes}
                />
              </Box>
            </Box>
          </Box>
          {(canRemove || hasFile) && !isCompleted && !isMobile && (
            <Typography sx={{ marginLeft: 0.5 }} variant={isMobile ? 'airbnbBody3' : 'airbnbBody1'}>
              • <span onClick={onRemoveItemClick} className={classes.blueText}>Remover</span>
            </Typography>
          )}
          {isMobile && !isCompleted && (
            <ResponsiveActionButtons
              hasFile={hasFile}
              rootProps={getRootProps}
              isCompleted={isCompleted}
              canRemove={canRemove}
              removeItem={onRemoveItemClick}
            />
          )}
        </Box>
      </Box>
    </Box>
  )
})
